import { Card, CardContent, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { useFetchLogSources } from '@/features/LogSourcesCreate/LogSourcesCreate.hooks';

import { formatSourceName } from '../../Devices.service';
import type { LogSourcesCardProps } from './LogSourcesCard.types';

/**
 * Converts a source key to a display label, handling wildcard module entries.
 *
 * @param source Source key from connection inputs.
 * @param sourceModules Source module lookup map.
 * @returns Display label for the source.
 */
const toSourceLabel = (
  source: string,
  sourceModules: Record<string, string>
): string => {
  const moduleKey = source.replace('*', '');
  if (source.includes('*') && sourceModules[moduleKey]) {
    return `${sourceModules[moduleKey]}*`;
  }

  return sourceModules[source] || source;
};

/**
 * Renders labels for configured log sources associated with a device.
 *
 * @param props Component properties.
 * @returns Log sources card view.
 */
export const LogSourcesCard = ({ data }: LogSourcesCardProps) => {
  const { data: logSourcesData } = useFetchLogSources();

  // map the source modules definition to look up set with key -> user friendly name
  // for example: "cisco_wlc" -> "Cisco - Wireless Lan Controller"
  const sourceModules = useMemo(() => {
    const sourceModuleDef =
      logSourcesData?.[0].connection_input_schema.modules || [];

    const modules: Record<string, string> = {};
    sourceModuleDef.forEach((module) => {
      const { name, vendor, product, data_streams } = module;
      modules[name] = `${vendor} - ${product}`;

      (data_streams || []).forEach((stream) => {
        modules[stream.name] =
          stream.stream_name || formatSourceName(stream.name);
      });
    });

    return modules;
  }, [logSourcesData]);

  const sourcesLabels = useMemo(() => {
    const sources =
      (data.sources?.[0]?.connection_inputs as { sources?: string[] })
        ?.sources || [];

    // keys can contain wildcards to indicate all streams should be included
    // for example: "1password*" will include all streams from the 1password module
    // so we need to handle the lookup for those keys
    return sources.sort().map((source) => toSourceLabel(source, sourceModules));
  }, [sourceModules, data.sources]);

  return (
    <Card variant="outlined" style={{ maxHeight: '8rem', overflow: 'auto' }}>
      <CardContent>
        <Stack spacing={0.5}>
          {sourcesLabels.map((source: string) => (
            <Typography key={source} sx={{ mt: 1, mb: 0.5 }}>
              {source}
            </Typography>
          )) || null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LogSourcesCard;
